import { Show } from "solid-js"
import { useNavigate } from "@solidjs/router"
import { useLanguage } from "@/context/language"
import { BackLabel } from "@/components/back-label"
import { useCoworkAuth } from "@/cowork/auth-store"
import { publicRegistrationEnabled } from "@/cowork/public-routes"
import { authErrorKeys, passwordValidationError } from "@/cowork/auth-errors"
import {
  authErrorClass,
  authFaintStyle,
  authInputClass,
  authLabelClass,
  authLabelStyle,
  authLinkClass,
  authPrimaryButtonClass,
  authPrimaryButtonStyle,
  CoworkAuthHeading,
  CoworkAuthShell,
} from "@/cowork/auth-shell"

export function CoworkAuthPage(props: { registration: boolean }) {
  const auth = useCoworkAuth()
  const language = useLanguage()
  const navigate = useNavigate()
  const errorMessage = () => {
    if (Object.hasOwn(authErrorKeys, auth.state.error)) {
      return language.t(authErrorKeys[auth.state.error as keyof typeof authErrorKeys])
    }
    return auth.state.error
  }
  const passwordInvalid = () => auth.state.error.startsWith("auth.password_")
  const submit = (event: SubmitEvent) => {
    event.preventDefault()
    void (props.registration
      ? auth.signup(auth.state.email, auth.state.password, auth.state.fullName, auth.state.organization)
      : auth.login(auth.state.email, auth.state.password))
  }
  const switchMode = () => {
    auth.setError("")
    auth.dismissVerificationNotice()
    navigate(props.registration ? "/" : "/register")
  }

  return (
    <CoworkAuthShell>
      <Show when={auth.state.verificationSentTo} fallback={<AuthForm />}>
        <CoworkAuthHeading
          title={language.t("cowork.login.verificationSentTitle")}
          description={language.t("cowork.login.verificationSentDescription", { email: auth.state.verificationSentTo })}
        />
        <p class="text-13-regular leading-5" style={authFaintStyle}>
          {language.t("cowork.login.verificationSentHint")}
        </p>
        <Show when={auth.state.error}>
          <div role="alert" class={authErrorClass}>
            {errorMessage()}
          </div>
        </Show>
        <button
          type="button"
          class={authPrimaryButtonClass}
          style={authPrimaryButtonStyle}
          disabled={auth.state.submitting}
          onClick={() => void auth.resend()}
        >
          {auth.state.submitting ? language.t("cowork.login.loading") : language.t("cowork.login.resendVerification")}
        </button>
        <p class="mt-6 text-center text-13-regular" style={authFaintStyle}>
          <button
            type="button"
            class={authLinkClass}
            onClick={() => {
              auth.dismissVerificationNotice()
              navigate("/")
            }}
          >
            <BackLabel>{language.t("cowork.login.backToSignin")}</BackLabel>
          </button>
        </p>
      </Show>
    </CoworkAuthShell>
  )

  function AuthForm() {
    return (
      <form onSubmit={submit}>
        <CoworkAuthHeading
          title={language.t(props.registration ? "cowork.login.signupTitle" : "cowork.login.signinTitle")}
          description={language.t(
            props.registration ? "cowork.login.signupDescription" : "cowork.login.signinDescription",
          )}
        />
        <Show when={props.registration}>
          <label class={authLabelClass} style={authLabelStyle}>
            {language.t("cowork.login.fullName")}
            <input
              class={authInputClass}
              type="text"
              autocomplete="name"
              required
              value={auth.state.fullName}
              onInput={(event) => auth.setFullName(event.currentTarget.value)}
              placeholder={language.t("cowork.login.fullNamePlaceholder")}
            />
          </label>
        </Show>
        <label class={authLabelClass} style={authLabelStyle}>
          {language.t(props.registration ? "cowork.login.workEmail" : "cowork.login.email")}
          <input
            class={authInputClass}
            type="email"
            autocomplete="email"
            required
            value={auth.state.email}
            onInput={(event) => auth.setEmail(event.currentTarget.value)}
            placeholder={language.t("cowork.login.emailPlaceholder")}
          />
        </label>
        <Show when={props.registration}>
          <label class={authLabelClass} style={authLabelStyle}>
            {language.t("cowork.login.organization")}
            <input
              class={authInputClass}
              type="text"
              autocomplete="organization"
              aria-describedby="cowork-organization-hint"
              required
              value={auth.state.organization}
              onInput={(event) => auth.setOrganization(event.currentTarget.value)}
              placeholder={language.t("cowork.login.organizationPlaceholder")}
            />
          </label>
          <p id="cowork-organization-hint" class="-mt-2 mb-4 text-12-regular leading-5" style={authFaintStyle}>
            {language.t("cowork.login.organizationHint")}
          </p>
        </Show>
        <label class="mb-2 flex flex-col gap-2 text-[14px] font-medium" style={authLabelStyle}>
          {language.t("cowork.login.password")}
          <input
            class={authInputClass}
            type="password"
            autocomplete={props.registration ? "new-password" : "current-password"}
            aria-invalid={passwordInvalid() || undefined}
            aria-describedby={
              passwordInvalid() ? "cowork-auth-error" : props.registration ? "cowork-password-hint" : undefined
            }
            minlength="8"
            required
            value={auth.state.password}
            onInvalid={(event) => {
              event.preventDefault()
              auth.setError(passwordValidationError(event.currentTarget.value) || "auth.password_invalid")
              event.currentTarget.focus()
            }}
            onInput={(event) => auth.setPassword(event.currentTarget.value)}
            placeholder={language.t(
              props.registration ? "cowork.login.passwordPlaceholder" : "cowork.login.signinPasswordPlaceholder",
            )}
          />
        </label>
        <Show when={props.registration}>
          <p id="cowork-password-hint" class="text-12-regular leading-5" style={authFaintStyle}>
            {language.t("cowork.login.passwordPlaceholder")}
          </p>
        </Show>
        <Show when={!props.registration}>
          <button type="button" class={`${authLinkClass} self-end text-[13px]`} onClick={() => navigate("/reset")}>
            {language.t("cowork.login.forgotPassword")}
          </button>
        </Show>
        <Show when={auth.state.error}>
          <div id="cowork-auth-error" role="alert" class={authErrorClass}>
            {errorMessage()}
            <Show when={auth.state.unverifiedEmail}>
              {" "}
              <button type="button" class="font-[600] underline" onClick={() => void auth.resend()}>
                {language.t("cowork.login.resendVerification")}
              </button>
            </Show>
          </div>
        </Show>
        <button
          type="submit"
          class={authPrimaryButtonClass}
          style={authPrimaryButtonStyle}
          disabled={auth.state.submitting}
        >
          {auth.state.submitting
            ? language.t("cowork.login.loading")
            : language.t(props.registration ? "cowork.login.signupButton" : "cowork.login.signinButton")}
        </button>
        <Show
          when={props.registration || publicRegistrationEnabled()}
          fallback={
            <p class="mt-6 text-center text-13-regular" style={authFaintStyle}>
              {language.t("cowork.login.registrationInviteOnly")}
            </p>
          }
        >
          <p class="mt-6 text-center text-13-regular" style={authFaintStyle}>
            {language.t(props.registration ? "cowork.login.alreadyRegistered" : "cowork.login.newToSuperCowork")}{" "}
            <button type="button" class={authLinkClass} onClick={switchMode}>
              {language.t(props.registration ? "cowork.login.signinLink" : "cowork.login.createWorkspaceLink")}
            </button>
          </p>
        </Show>
      </form>
    )
  }
}
